import type { paths as ApiPaths } from '@dotit/contracts'
import type { ErrorEnvelope } from './types'

export type ContractWitness = ApiPaths

let csrfToken: string | undefined

export function setCsrfToken(value: string | undefined): void {
  csrfToken = value
}

export class ApiError extends Error {
  public constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly requestId?: string,
    public readonly fieldErrors?: Record<string, string[]>
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = init.method?.toUpperCase() ?? 'GET'
  const headers = new Headers(init.headers)
  if (init.body && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json')
  }
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method) && csrfToken) {
    headers.set('X-CSRF-Token', csrfToken)
  }
  const response = await fetch(`/api/v1${path}`, {
    ...init,
    headers,
    credentials: 'include',
  })
  if (!response.ok) {
    let envelope: ErrorEnvelope | undefined
    try {
      envelope = (await response.json()) as ErrorEnvelope
    } catch {
      // The reverse proxy may have generated a non-JSON error.
    }
    throw new ApiError(
      response.status,
      envelope?.error.code ?? 'REQUEST_FAILED',
      envelope?.error.message ?? `Request failed with ${response.status}`,
      envelope?.error.requestId,
      envelope?.error.fieldErrors
    )
  }
  if (response.status === 204) {
    return undefined as T
  }
  return (await response.json()) as T
}

export function jsonBody(value: unknown): string {
  return JSON.stringify(value)
}
